<template>
  <div class="w-full h-full flex flex-col overflow-hidden bg-dark-secondary">
    <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar" style="scrollbar-gutter: stable;">
      <div class="max-w-[1600px] mx-auto w-full px-6 lg:px-8 py-6 lg:py-8">

        <!-- Header -->
        <div class="flex items-center justify-between mb-8">
          <div>
            <h1 class="text-2xl font-bold text-dark-primary tracking-tight">Dashboard</h1>
            <p class="text-sm text-dark-secondary mt-1 flex items-center gap-2">
              Security intelligence overview
              <span class="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-accent/10 text-accent rounded-full font-medium">
                <div class="w-1.5 h-1.5 bg-accent rounded-full animate-pulse"></div>
                Live
              </span>
            </p>
          </div>
          <div class="flex items-center gap-1 p-1 bg-dark-primary border border-dark rounded-xl shadow-sm">
            <button
              v-for="p in periods" :key="p.value"
              @click="setPeriod(p.value)"
              class="px-4 py-2 rounded-lg text-xs font-semibold transition-all relative"
              :class="period === p.value ? 'bg-accent text-white shadow-md' : 'text-dark-secondary hover:text-dark-primary hover:bg-dark-tertiary/50'"
            >
              {{ p.label }}
              <div v-if="period === p.value" class="absolute inset-0 bg-accent rounded-lg opacity-20 animate-pulse"></div>
            </button>
          </div>
        </div>

        <!-- KPI Cards -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          
          <!-- Total Sessions -->
          <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm flex flex-col justify-center">
            <p class="text-[11px] text-dark-secondary font-bold uppercase tracking-wider mb-2">Total Sessions</p>
            <p class="text-3xl font-bold text-dark-primary" :style="loading ? 'opacity:0.4' : ''">
              {{ data?.risk?.total ?? '—' }}
            </p>
            <p class="text-xs text-dark-tertiary mt-1">{{ data?.coverage?.completion_rate ?? '—' }}% analyzed</p>
          </div>
          
          <!-- Flagged -->
          <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm flex flex-col justify-center">
            <p class="text-[11px] text-dark-secondary font-bold uppercase tracking-wider mb-2">Flagged</p>
            <p class="text-3xl font-bold text-error" :style="loading ? 'opacity:0.4' : ''">
              {{ data?.risk?.flagged ?? '—' }}
            </p>
            <p class="text-xs text-dark-tertiary mt-1">
              <span class="text-error font-semibold">{{ data?.risk?.critical ?? 0 }}</span> critical ·
              <span class="text-warning font-semibold">{{ data?.risk?.high ?? 0 }}</span> high
            </p>
          </div>
          
          <!-- TTPs Detected -->
          <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm flex flex-col justify-center">
            <p class="text-[11px] text-dark-secondary font-bold uppercase tracking-wider mb-2">TTPs Detected</p>
            <p class="text-3xl font-bold text-warning" :style="loading ? 'opacity:0.4' : ''">
              {{ data?.ttps?.total ?? '—' }}
            </p>
            <p class="text-xs text-dark-tertiary mt-1">{{ data?.ttps?.top_tactics?.length ?? 0 }} unique tactics</p>
          </div>
          
          <!-- Coverage -->
          <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm flex flex-col justify-center">
            <p class="text-[11px] text-dark-secondary font-bold uppercase tracking-wider mb-2">Coverage</p>
            <p class="text-3xl font-bold text-accent" :style="loading ? 'opacity:0.4' : ''">
              {{ data?.coverage?.completion_rate ?? '—' }}<span class="text-lg" v-if="data">%</span>
            </p>
            <p class="text-xs text-dark-tertiary mt-1">
              <template v-if="(data?.coverage?.failed ?? 0) > 0">
                <span class="text-warning font-semibold">{{ data.coverage.failed }}</span> failed
              </template>
              <template v-else>Processing pipeline active</template>
            </p>
          </div>
        </div>

        <!-- Row 2: Trend + Risk Breakdown -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">

          <!-- Trend chart -->
          <div class="lg:col-span-2 bg-dark-primary border border-dark rounded-xl p-5 shadow-sm hover:shadow-lg transition-all">
            <div class="flex items-center justify-between mb-4">
              <div>
                <h2 class="text-sm font-bold text-dark-primary">Risk Trend</h2>
                <p class="text-xs text-dark-tertiary mt-0.5">Sessions by severity over time</p>
              </div>
              <div class="flex items-center gap-3 text-xs text-dark-secondary">
                <span class="flex items-center gap-1.5"><span class="w-2 h-2 rounded-full bg-error inline-block"></span>Critical</span>
                <span class="flex items-center gap-1.5"><span class="w-2 h-2 rounded-full bg-warning inline-block"></span>High</span>
                <span class="flex items-center gap-1.5"><span class="w-2 h-2 rounded-full text-accent" style="background:var(--accent)" ></span>Medium</span>
              </div>
            </div>
            <div class="relative bg-dark-secondary/20 rounded-lg p-2" style="height:140px;">
              <svg v-if="data?.trend?.length" class="w-full h-full" viewBox="0 0 400 120" preserveAspectRatio="none">
                <!-- Subtle grid -->
                <defs>
                  <pattern id="grid" width="50" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 50 0 L 0 0 0 40" fill="none" stroke="var(--border-color)" stroke-width="0.5" opacity="0.2"/>
                  </pattern>
                </defs>
                <rect width="400" height="120" fill="url(#grid)"/>
                
                <!-- Enhanced gradients -->
                <defs>
                  <linearGradient id="criticalGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style="stop-color:#ef4444;stop-opacity:0.2"/>
                    <stop offset="100%" style="stop-color:#ef4444;stop-opacity:0.05"/>
                  </linearGradient>
                  <linearGradient id="highGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style="stop-color:#f59e0b;stop-opacity:0.2"/>
                    <stop offset="100%" style="stop-color:#f59e0b;stop-opacity:0.05"/>
                  </linearGradient>
                  <linearGradient id="mediumGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style="stop-color:var(--accent);stop-opacity:0.2"/>
                    <stop offset="100%" style="stop-color:var(--accent);stop-opacity:0.05"/>
                  </linearGradient>
                </defs>
                
                <path :d="areaPath('critical')" fill="url(#criticalGrad)" />
                <path :d="linePath('critical')" fill="none" stroke="#ef4444" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
                <path :d="areaPath('high')" fill="url(#highGrad)" />
                <path :d="linePath('high')" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
                <path :d="areaPath('medium')" fill="url(#mediumGrad)" />
                <path :d="linePath('medium')" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
              </svg>
              <div v-if="data?.trend?.length" class="absolute bottom-0 left-2 right-2 flex justify-between">
                <span v-for="(lbl, i) in trendLabels" :key="i" class="text-[9px] text-dark-tertiary">{{ lbl }}</span>
              </div>
              <div v-else class="flex items-center justify-center h-full text-sm text-dark-tertiary">No trend data</div>
            </div>
          </div>

          <!-- Risk Breakdown -->
          <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm hover:shadow-lg transition-all">
            <h2 class="text-sm font-bold text-dark-primary mb-1">Risk Breakdown</h2>
            <p class="text-xs text-dark-tertiary mb-4">Distribution for {{ period }}</p>
            <div class="space-y-3">
              <div v-for="lvl in riskLevels" :key="lvl.key">
                <div class="flex items-center justify-between mb-1">
                  <span class="text-xs font-semibold uppercase tracking-wide" :class="lvl.text">{{ lvl.label }}</span>
                  <div class="flex items-center gap-2">
                    <span class="text-xs font-bold text-dark-primary">{{ data?.risk?.[lvl.key] ?? 0 }}</span>
                    <span class="text-[10px] text-dark-tertiary">{{ riskPct(lvl.key) }}%</span>
                  </div>
                </div>
                <div class="h-1.5 bg-dark rounded-full overflow-hidden">
                  <div class="h-full rounded-full transition-all duration-700 relative" :class="lvl.bg"
                    :style="{ width: riskPct(lvl.key) + '%' }"
                  >
                    <div class="absolute inset-0 bg-white opacity-20 -skew-x-12"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Row 3: Requires Attention -->
        <div class="bg-dark-primary border border-dark rounded-xl shadow-sm mb-6 overflow-hidden hover:shadow-lg transition-all">
          <div class="flex items-center justify-between px-5 py-4 border-b border-dark bg-gradient-to-r from-dark-primary to-dark-secondary/10">
            <div>
              <h2 class="text-sm font-bold text-dark-primary flex items-center gap-2">
                Requires Attention
                <div v-if="data?.recent_flagged?.total > 0" class="w-2 h-2 bg-error rounded-full animate-pulse"></div>
              </h2>
              <p class="text-xs text-dark-tertiary mt-0.5">Critical and high risk sessions · {{ data?.recent_flagged?.total ?? 0 }} total</p>
            </div>
            <div class="flex items-center gap-1">
              <button @click="prevPage" :disabled="flaggedOffset === 0"
                class="p-1.5 rounded-lg text-dark-secondary hover:text-dark-primary hover:bg-dark-tertiary disabled:opacity-30 transition">
                <ChevronLeft class="w-4 h-4" />
              </button>
              <span class="text-xs text-dark-tertiary px-2 py-1 bg-dark-tertiary rounded font-medium">
                {{ flaggedOffset + 1 }}–{{ Math.min(flaggedOffset + flaggedLimit, data?.recent_flagged?.total ?? 0) }}
              </span>
              <button @click="nextPage" :disabled="!hasMoreFlagged"
                class="p-1.5 rounded-lg text-dark-secondary hover:text-dark-primary hover:bg-dark-tertiary disabled:opacity-30 transition">
                <ChevronRight class="w-4 h-4" />
              </button>
            </div>
          </div>

          <div v-if="!data?.recent_flagged?.data?.length" class="flex items-center justify-center py-10 text-sm text-dark-tertiary">
            <div class="text-center">
              <ShieldCheck class="w-6 h-6 mr-auto ml-auto mb-2 opacity-50" style="color:var(--accent)" />
              <p>No flagged sessions in this period</p>
              <p class="text-xs text-dark-quaternary mt-1">All sessions are within acceptable risk parameters</p>
            </div>
          </div>

          <div v-else>
            <!-- Table header -->
            <div class="grid items-center gap-3 px-5 py-2.5 bg-dark-secondary/30 border-y border-dark text-[10px] font-bold uppercase tracking-wider text-dark-tertiary"
                 style="grid-template-columns: 60px 1fr 90px 110px 110px 50px 20px;">
              <div>Risk</div>
              <div>Summary</div>
              <div>User</div>
              <div>Origin IP</div>
              <div>Target IP</div>
              <div class="text-center">TTPs</div>
              <div></div>
            </div>

            <!-- Table rows -->
            <div class="divide-y divide-dark">
              <div v-for="s in data.recent_flagged.data" :key="s.session_id || s.id"
                @click="openSession(s.session_id || s.id)"
                class="grid items-center gap-3 px-5 py-3 hover:bg-dark-tertiary/50 cursor-pointer transition group"
                style="grid-template-columns: 60px 1fr 90px 110px 110px 50px 20px;"
              >
                <!-- Col 1: Risk badge -->
                <div>
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold uppercase relative inline-block"
                    :class="s.risk === 'critical' ? 'bg-error/15 text-error' : 'bg-warning/15 text-warning'">
                    {{ s.risk }}
                    <div v-if="s.risk === 'critical'" class="absolute -top-1 -right-1 w-2 h-2 bg-error rounded-full animate-ping"></div>
                  </span>
                </div>

                <!-- Col 2: Summary -->
                <div class="min-w-0">
                  <p class="text-xs text-dark-primary leading-snug line-clamp-2">{{ s.narrative || s.verdict || '—' }}</p>
                </div>

                <!-- Col 3: User -->
                <div class="min-w-0">
                  <div class="flex items-center gap-1.5">
                    <User class="w-3 h-3 text-accent flex-shrink-0" />
                    <span class="text-xs font-medium text-dark-primary truncate">{{ s.pam_username || '—' }}</span>
                  </div>
                </div>

                <!-- Col 4: Origin IP -->
                <div class="min-w-0">
                  <span class="text-xs text-dark-primary font-mono truncate block">{{ s.origin_ip || '—' }}</span>
                </div>

                <!-- Col 5: Target IP -->
                <div class="min-w-0">
                  <div class="flex items-center gap-1.5">
                    <Server class="w-3 h-3 text-dark-secondary flex-shrink-0" />
                    <span class="text-xs text-dark-primary font-mono truncate">{{ s.target_ip || s.target_host || '—' }}</span>
                  </div>
                </div>

                <!-- Col 6: TTPs count -->
                <div class="text-center">
                  <p class="text-lg font-bold text-error leading-none">{{ s.ttp_count ?? 0 }}</p>
                </div>

                <!-- Col 7: Arrow -->
                <div>
                  <ArrowRight class="w-4 h-4 text-dark-tertiary opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Row 4: Top Tactics + Compliance -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <!-- Top Tactics -->
          <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm">
            <div class="flex items-center justify-between mb-4">
              <div>
                <h2 class="text-sm font-bold text-dark-primary">Top Tactics</h2>
                <p class="text-xs text-dark-tertiary mt-0.5">Most frequently detected MITRE ATT&CK tactics</p>
              </div>
              <Target class="w-4 h-4 text-error opacity-60" />
            </div>

            <div v-if="!data?.ttps?.top_tactics?.length" class="flex items-center justify-center py-8 text-xs text-dark-tertiary">
              No tactics detected in this period
            </div>

            <div v-else class="space-y-3">
              <div v-for="(tactic, idx) in data.ttps.top_tactics.slice(0, 5)" :key="idx"
                   class="group">
                <div class="flex items-center justify-between mb-1.5 gap-3">
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <span v-if="getTacticId(tactic)" class="text-[10px] bg-error/10 text-error border border-error/30 rounded px-1.5 py-0.5 font-mono font-bold flex-shrink-0">
                      {{ getTacticId(tactic) }}
                    </span>
                    <span class="text-xs font-medium text-dark-primary truncate">{{ getTacticName(tactic) }}</span>
                  </div>
                  <div class="flex items-center gap-2 flex-shrink-0">
                    <span class="text-sm font-bold text-error tabular-nums">{{ tactic.count }}</span>
                    <span class="text-[10px] text-dark-tertiary tabular-nums w-8 text-right">{{ tacticPct(tactic.count) }}%</span>
                  </div>
                </div>
                <div class="h-1.5 bg-dark rounded-full overflow-hidden">
                  <div class="h-full bg-error rounded-full transition-all duration-700"
                    :style="{ width: tacticPct(tactic.count) + '%' }"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Compliance Impact -->
          <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm">
            <div class="flex items-center justify-between mb-4">
              <div>
                <h2 class="text-sm font-bold text-dark-primary">Compliance Impact</h2>
                <p class="text-xs text-dark-tertiary mt-0.5">Most frequently violated regulatory frameworks</p>
              </div>
              <FileWarning class="w-4 h-4 text-error opacity-60" />
            </div>

            <div v-if="!data?.compliance?.top_frameworks_violated?.length" class="flex items-center justify-center py-8 text-xs text-dark-tertiary">
              No compliance violations in this period
            </div>

            <div v-else class="space-y-3">
              <div v-for="fw in data.compliance.top_frameworks_violated.slice(0, 5)" :key="fw.framework"
                   class="group">
                <div class="flex items-center justify-between mb-1.5 gap-3">
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <span class="px-1.5 py-0.5 text-[10px] rounded font-bold flex-shrink-0"
                          :class="getFrameworkStyle(fw.framework)">
                      {{ getFrameworkShortName(fw.framework) }}
                    </span>
                    <span class="text-xs text-dark-secondary truncate">{{ fw.framework }}</span>
                  </div>
                  <div class="flex items-center gap-2 flex-shrink-0">
                    <span class="text-sm font-bold text-error tabular-nums">{{ fw.count }}</span>
                    <span class="text-[10px] text-dark-tertiary tabular-nums w-8 text-right">{{ compliancePct(fw.count) }}%</span>
                  </div>
                </div>
                <div class="h-1.5 bg-dark rounded-full overflow-hidden">
                  <div class="h-full bg-error rounded-full transition-all duration-700"
                    :style="{ width: compliancePct(fw.count) + '%' }"
                  />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { ChevronLeft, ChevronRight, ArrowRight, ShieldCheck, User, Target, FileWarning, Server, FileUp } from "lucide-vue-next";
import { apiClient } from "../services/apiClient";
import { useAuthStore } from "../stores/authStore";

const router     = useRouter();
const authStore  = useAuthStore();

const PERIOD_KEY  = "dashboard_period";
const flaggedLimit = 5;

const periods = [
  { label: "24h", value: "24h" },
  { label: "7d",  value: "7d"  },
  { label: "30d", value: "30d" },
];

const period        = ref(localStorage.getItem(PERIOD_KEY) || "7d");
const loading       = ref(false);
const data          = ref(null);
const flaggedOffset = ref(0);

async function load() {
  if (!authStore.apiBase) return;
  loading.value = true;
  try {
    data.value = await apiClient(
      `/dashboard?period=${period.value}&limit=${flaggedLimit}&offset=${flaggedOffset.value}`
    );
  } catch (err) {
    console.error("[Dashboard]", err);
  } finally {
    loading.value = false;
  }
}

function setPeriod(p) {
  period.value = p;
  flaggedOffset.value = 0;
  localStorage.setItem(PERIOD_KEY, p);
}

watch(period, load);
watch(() => authStore.apiBase, (base) => { if (base) { flaggedOffset.value = 0; load(); } });
onMounted(() => { if (authStore.apiBase) load(); });

// Pagination
const hasMoreFlagged = computed(() => {
  const total = data.value?.recent_flagged?.total ?? 0;
  return flaggedOffset.value + flaggedLimit < total;
});

async function nextPage() {
  if (!hasMoreFlagged.value) return;
  flaggedOffset.value += flaggedLimit;
  await load();
}

async function prevPage() {
  if (flaggedOffset.value === 0) return;
  flaggedOffset.value = Math.max(0, flaggedOffset.value - flaggedLimit);
  await load();
}

function openSession(id) {
  router.push({ name: "investigation", params: { id } });
}

// Risk
const riskLevels = [
  { key: "critical", label: "Critical", text: "text-error",           bg: "bg-error"           },
  { key: "high",     label: "High",     text: "text-warning",         bg: "bg-warning"         },
  { key: "medium",   label: "Medium",   text: "text-accent",          bg: "bg-accent"          },
  { key: "low",      label: "Low",      text: "text-dark-secondary",  bg: "bg-dark-secondary"  },
];

function riskPct(key) {
  const total = data.value?.risk?.total ?? 0;
  if (!total) return 0;
  return Math.round(((data.value?.risk?.[key] ?? 0) / total) * 100);
}

// Trend SVG
const maxTrendVal = computed(() => {
  if (!data.value?.trend?.length) return 1;
  return Math.max(1, ...data.value.trend.map(p => Math.max(p.critical || 0, p.high || 0, p.medium || 0, p.low || 0)));
});

function linePath(key) {
  const pts = data.value?.trend ?? [];
  if (!pts.length) return "";
  const W = 400, H = 120, PAD = 8;
  const step = W / Math.max(pts.length - 1, 1);
  return pts.map((p, i) => {
    const x = i * step;
    const y = H - PAD - ((p[key] || 0) / maxTrendVal.value) * (H - PAD * 2);
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
}

function areaPath(key) {
  const pts = data.value?.trend ?? [];
  if (!pts.length) return "";
  const W = 400, H = 120, PAD = 8;
  const step = W / Math.max(pts.length - 1, 1);
  const coords = pts.map((p, i) => {
    const x = (i * step).toFixed(1);
    const y = (H - PAD - ((p[key] || 0) / maxTrendVal.value) * (H - PAD * 2)).toFixed(1);
    return `${x},${y}`;
  }).join(" L");
  const lastX = ((pts.length - 1) * step).toFixed(1);
  return `M0,${H} L${coords} L${lastX},${H} Z`;
}

const trendLabels = computed(() => {
  const pts = data.value?.trend ?? [];
  if (pts.length <= 1) return pts.map(p => p.date?.slice(5) ?? "");
  const indices = [0];
  const step = Math.max(1, Math.floor((pts.length - 1) / 4));
  for (let i = step; i < pts.length - 1; i += step) indices.push(i);
  indices.push(pts.length - 1);
  return [...new Set(indices)].map(i => {
    const d = pts[i]?.date ?? "";
    if (period.value === "24h") return d.slice(11, 16) || d;
    return d.slice(5) || d;
  });
});

// Tactics
const maxTacticCount = computed(() =>
  Math.max(1, ...(data.value?.ttps?.top_tactics?.map(t => t.count) ?? [1]))
);
function tacticPct(count) { return Math.round((count / maxTacticCount.value) * 100); }

// Map known tactic IDs to names (fallback when API only returns ID)
const TACTIC_NAMES = {
  'TA0001': 'Initial Access',
  'TA0002': 'Execution',
  'TA0003': 'Persistence',
  'TA0004': 'Privilege Escalation',
  'TA0005': 'Defense Evasion',
  'TA0006': 'Credential Access',
  'TA0007': 'Discovery',
  'TA0008': 'Lateral Movement',
  'TA0009': 'Collection',
  'TA0010': 'Exfiltration',
  'TA0011': 'Command and Control',
  'TA0040': 'Impact',
  'TA0042': 'Resource Development',
  'TA0043': 'Reconnaissance',
};

function getTacticId(tactic) {
  return tactic?.tactic_id || tactic?.ta_id || tactic?.id ||
         (tactic?.name?.match(/TA\d{4}/i)?.[0]) ||
         (tactic?.tactic?.match(/TA\d{4}/i)?.[0]) ||
         '';
}

function getTacticName(tactic) {
  const name = tactic?.tactic_name || tactic?.name || tactic?.label || tactic?.tactic;
  if (name && !/^TA\d{4}$/i.test(name)) return name;
  // Fallback: use known names map for IDs
  const id = getTacticId(tactic);
  return TACTIC_NAMES[id?.toUpperCase()] || id || 'Unknown';
}

// Compliance
const maxComplianceCount = computed(() =>
  Math.max(1, ...(data.value?.compliance?.top_frameworks_violated?.map(f => f.count) ?? [1]))
);
function compliancePct(count) { return Math.round((count / maxComplianceCount.value) * 100); }

function getFrameworkShortName(framework) {
  const map = {
    'ISO/IEC 27001:2022':                     '27001',
    'NIST SP 800-53 Rev. 5':                  'NIST',
    'NIST SP 800-53 Rev.5':                   'NIST',
    'NIST Cybersecurity Framework (CSF) 2.0': 'CSF',
    'CIS Controls v8':                        'CIS',
    'SOC 2 (Trust Service Criteria)':         'SOC 2',
    'SOC 2':                                  'SOC 2',
    'ISA/IEC 62443':                          '62443',
    'MITRE D3FEND':                           'D3FEND',
    'LGPD (Lei Geral de Proteção de Dados)':  'LGPD',
    'GDPR (General Data Protection Regulation)': 'GDPR',
  };
  return map[framework] || framework.split(' ').slice(0, 2).join(' ');
}

function getFrameworkStyle(framework) {
  const f = (framework || '').toLowerCase();
  if (f.includes('iso'))                        return 'bg-purple-600 text-white';
  if (f.includes('nist') && f.includes('csf'))  return 'bg-sky-600 text-white';
  if (f.includes('nist'))                       return 'bg-blue-700 text-white';
  if (f.includes('cis'))                        return 'bg-orange-600 text-white';
  if (f.includes('soc'))                        return 'bg-amber-500 text-white';
  if (f.includes('isa') || f.includes('62443')) return 'bg-indigo-600 text-white';
  if (f.includes('d3fend'))                     return 'bg-green-600 text-white';
  if (f.includes('gdpr'))                       return 'bg-teal-600 text-white';
  if (f.includes('lgpd'))                       return 'bg-teal-700 text-white';
  if (f.includes('pci'))                        return 'bg-orange-700 text-white';
  if (f.includes('hipaa'))                      return 'bg-blue-600 text-white';
  if (f.includes('sox'))                        return 'bg-amber-600 text-white';
  return 'bg-slate-600 text-white';
}

function shortFramework(name) {
  return getFrameworkShortName(name);
}
</script>

<style scoped>
.line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.custom-scrollbar { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.1) transparent; }
.custom-scrollbar::-webkit-scrollbar       { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }

/* Enhanced hover and animation effects */
.transition-all {
  transition: all 0.2s ease-in-out;
}

.group:hover .group-hover\:translate-x-1 {
  transform: translateX(0.25rem);
}

.group:hover .group-hover\:opacity-100 {
  opacity: 1;
}

/* Subtle animations */
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

.animate-ping {
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}
</style>